import type {ReactNode} from 'react';
import React from 'react';

import {Card, Heading, Icon, Text} from '@albert-io/atomic';

import type {QuestionModelV3, QuestionSetModelV1, SubjectModelV2} from '@albert-io/models';

import {QuestionWrapper, HeadingBar} from 'client/components/QuestionTypes/shared/ContentBlocks';

import {
  RESTRICTED_QUESTION_TITLE,
  STUDENT_LOCKED_QUESTION_HEADING,
  STUDENT_LOCKED_QUESTION_MESSAGE
} from './studentContentLock';

interface NavigationProps {
  disabled: boolean;
  handleNavigation: () => void;
}

interface Props {
  actions?: ReactNode;
  activeQuestion: QuestionModelV3;
  activeQuestionSet: QuestionSetModelV1;
  activeSubject: SubjectModelV2;
  headingActions: ReactNode;
  navigateBackProps: NavigationProps | null;
  navigateNextProps: NavigationProps | null;
}

// ~400px: a readable card measure with no spacing-token equivalent.
const LOCK_CARD_MAX_WIDTH = 'max-w-[400px]';

// ~560px: the placeholder column width from the design; no spacing-token equivalent.
const SKELETON_MAX_WIDTH = 'max-w-[560px]';

// Decorative placeholder bars standing in for the hidden question content behind the lock card.
// The backend never sends the content, so these are static placeholders (not blurred real text);
// stable ids keep them out of the array-index-key rule. Widths vary to read as prose — arbitrary
// percentages (only w-1/2 of the fraction scale is used in this codebase) avoid partial bars
// silently falling back to full width.
const SKELETON_LINES = [
  'w-full',
  'w-full',
  'w-full',
  'w-[60%]',
  'w-full',
  'w-full',
  'w-full',
  'w-[70%]',
  'w-full',
  'w-full',
  'w-[50%]'
].map((width, index) => ({id: `skeleton-line-${index}`, width}));

/**
 * Block-and-message panel shown to a student in place of a restricted question inside an assignment
 * that has not been lock-authorized (admin-locked-assessments, Part 4). Swapped in for the whole
 * question renderer by `QuestionRenderer`, so the content is never revealed: there is no stem, no
 * answer input, and no explanation or graded/results state, and the question is non-interactive. The
 * student continues to the next question with the existing Next action; surrounding non-restricted
 * questions render normally.
 *
 * The body grid-stacks a centered "locked" card over decorative skeleton bars in one grid cell (the
 * hidden content is never sent by the backend, so the bars are placeholders, not blurred real text);
 * stacking lets the cell grow with whichever is taller, so the card's message can never be clipped.
 * The header title is still redacted via `modifyTitle` — `QuestionWrapper` injects the real
 * `activeQuestion` into the cloned `HeadingBar`, so without it the real title would render. Copy is
 * neutral and school-less — students are never shown the restricting school (`references/ui-patterns.md`).
 */
export default function BlockedForStudentQuestion({
  activeQuestion,
  activeQuestionSet,
  activeSubject,
  actions,
  navigateBackProps,
  navigateNextProps,
  headingActions
}: Props) {
  return (
    <QuestionWrapper
      question={activeQuestion}
      questionSet={activeQuestionSet}
      subject={activeSubject}
      heading={<HeadingBar question={undefined} questionSet={undefined} showDifficulty={false} />}
      // `QuestionWrapper` clones the heading with its own `showDifficulty`, overriding the value set
      // on the inline `HeadingBar`; set it here so the redacted header doesn't leak difficulty (#17843).
      showDifficulty={false}
      modifyTitle={() => RESTRICTED_QUESTION_TITLE}
      navigateBackProps={navigateBackProps}
      navigateNextProps={navigateNextProps}
      headingActions={headingActions}
    >
      <div className='grid p-x6'>
        <div
          className={`col-start-1 row-start-1 mx-auto flex w-full flex-col gap-x2 ${SKELETON_MAX_WIDTH}`}
          aria-hidden='true'
        >
          {SKELETON_LINES.map((line) => (
            <div
              key={line.id}
              className={`h-x1-half rounded-full bg-background-subtle ${line.width}`}
            />
          ))}
        </div>
        <div className='col-start-1 row-start-1 flex items-center justify-center'>
          <Card
            roundness='large'
            shadow='default'
            paddingLevel={4}
            className={`w-full ${LOCK_CARD_MAX_WIDTH}`}
          >
            <div className='flex flex-col items-center gap-x2 text-center'>
              <span className='flex items-center justify-center rounded-full bg-background-subtle p-x2'>
                <Icon
                  icon='lock'
                  iconStyle='solid'
                  color='secondary'
                  size='inherit'
                  className='text-xxl'
                />
              </span>
              <Heading as='h2' size='s'>
                {STUDENT_LOCKED_QUESTION_HEADING}
              </Heading>
              <Text>{STUDENT_LOCKED_QUESTION_MESSAGE}</Text>
            </div>
          </Card>
        </div>
      </div>
      {actions || null}
    </QuestionWrapper>
  );
}
